import { formatDate } from '@angular/common';
import { Component, ElementRef, EventEmitter, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MultiSelectorDetails } from 'src/app/model/multi-selector-details';
import { messages } from 'src/app/resources/messages';
import { DateRangeComponent } from '../date-range/date-range.component';
import { CatalogService } from 'src/app/services/catalog.service';
import { FormHelper } from 'src/app/Utility/FormHelper';
import { dateRange } from '../validators/daterange.validator';
import { Filter } from 'src/app/model/maker-checker/filter';
import { RequestStatus } from 'src/app/model/maker-checker/request-status';

@Component({
  selector: 'maker-checker-filter-form',
  templateUrl: './maker-checker-filter-form.component.html',
  styleUrls: ['./maker-checker-filter-form.component.scss']
})
export class MakerCheckerFilterFormComponent implements OnInit {

  @Output() filterQuery: EventEmitter<Filter> = new EventEmitter<Filter>();

  constructor(private catalogService: CatalogService) { }

  ngOnInit(): void {
    this.filterForm = new FormGroup({
      status: new FormControl(1, { nonNullable: true, validators: [Validators.required] }),
      fromDate: new FormControl<string>(''),
      toDate: new FormControl<string>(''),
      requester: new FormControl<string>(''),
      actionTakenBy: new FormControl<string>(''),
    }, {
      validators: Validators.compose([
        dateRange('fromDate', 'toDate', 90)])
    });
    this.getEmployeeData();
    this.getMakerCheckerRequestStatusData();
  }

  ngAfterViewInit() {
    this.setElementReferences();
  }

  @ViewChild(DateRangeComponent)
  public dateRangeComponent!: DateRangeComponent;
  @ViewChild('actionTakenByReference')
  public actionTakenByReference!: ElementRef;
  @ViewChild('requestersReference')
  public requestersReference!: ElementRef;
  public openRadioButtonReference!: ElementRef;
  public cancelledRadioButtonReference!: ElementRef;
  public approvedRadioButtonReference!: ElementRef;
  public rejectedRadioButtonReference!: ElementRef;

  @ViewChildren('radioOptions')
  public radioOptionsReference!: QueryList<ElementRef>;

  public requesters: MultiSelectorDetails[] = [];
  public approvers: MultiSelectorDetails[] = [];
  public requestStatus: RequestStatus[] = [];
  public filterForm!: FormGroup;
  public submitted: string = 'enabled';
  public msg = messages;
  public showActionTakenBy: boolean = true;
  public isDateRangeVisible: boolean = false;
  public expandedState: boolean = false;
  public selectedRequesterValue: any;
  public selectedActionTakenByValue: any;

  private emittedValues: Filter | undefined = undefined;

  public clearFilters(forceEmit: boolean = false): void {
    FormHelper.markAllControlUntouched(this.filterForm);
    this.openRadioButtonReference.nativeElement.setSelection(true);
    if (this.emittedValues != undefined || forceEmit) {
      this.emittedValues = undefined;
      let timer = setTimeout(() => {
        this.mapEmitOutput();
        clearTimeout(timer);
      }, 0)
    }
  }

  public expandedEvent(event: any) {
    this.expandedState = event.detail.expand;
    if (this.expandedState) {
      this.setAppliedFilters();
    }
  }

  public cancel() {
    this.expandedState = false;
  }

  public setSelectedStatus($event: any) {
    let status: number = parseInt($event.detail.value);
    this.filterForm.patchValue({ status: status });
    this.dateRangeComponent.reset();
    this.actionTakenByReference.nativeElement.resetSelectMenu();
    this.requestersReference.nativeElement.resetSelectMenu();
    this.selectedRequesterValue = {};
    this.selectedActionTakenByValue = {};
    switch (status) {
      case 2: //Cancelled
        this.formCancelledState();
        break;
      case 3: //Approved
      case 4: //Rejected
        this.formApprovedRejectedState();
        break;
      case 1: //Open
      default:
        this.formDefaultState();
        break;
    }
  }

  public applyFilter() {
    FormHelper.markAllControlTouched(this.filterForm)
    if (this.filterForm.invalid) {
      this.submitted = 'enabled';
    }
    else {
      this.emittedValues = this.mapEmitOutput();;
      this.expandedState = false;
    }
  }

  public setSelectedFromDate(selectedDate: string) {
    this.filterForm.patchValue({ fromDate: selectedDate })
  }

  public setSelectedToDate(selectedDate: string) {
    this.filterForm.patchValue({ toDate: selectedDate })
  }

  public fillDefaultDates(defaultDatesToSet: string[]) {
    this.filterForm.patchValue({
      fromDate: formatDate(defaultDatesToSet[0], 'yyyy-MM-dd', 'en-US')
    })
    this.filterForm.patchValue({
      toDate: formatDate(defaultDatesToSet[1], 'yyyy-MM-dd', 'en-US')
    })
  }
  public setSelectedRequester($event: any) {
    let selectedRequester = $event.detail?.selectedItemArray.map((x: MultiSelectorDetails) => x.id);
    this.filterForm.patchValue({
      requester: selectedRequester.length > 0 ? selectedRequester[0] : ''
    });
    this.requestersReference.nativeElement.closeSelectionPanel();
  }

  public setSelectedActionTakenBy($event: any) {
    let selectedActionTakenBy = $event.detail?.selectedItemArray.map((x: MultiSelectorDetails) => x.id);
    this.filterForm.patchValue({
      actionTakenBy: selectedActionTakenBy.length > 0 ? selectedActionTakenBy[0] : ''
    });
    this.actionTakenByReference.nativeElement.closeSelectionPanel();
  }

  private getEmployeeData() {
    this.catalogService.getEmployee().subscribe(response => {
      this.requesters = this.approvers = response.data.map(x => <MultiSelectorDetails><unknown>{
        id: x.xbcUserId,
        description: [x.userName]
      });
    })
  }

  private getMakerCheckerRequestStatusData() {
    this.catalogService.getMakerCheckerRequestStatus().subscribe(response => {
      this.requestStatus = response.data;
      this.mapEmitOutput();
    })
  }

  private setAppliedFilters() {
    if (this.emittedValues === undefined) {
      this.clearFilters();
    }
    else {
      switch (this.emittedValues.status.id) {
        case 1: //Open
          this.openRadioButtonReference.nativeElement.setSelection(true);
          break;
        case 2: //Cancelled
          this.cancelledRadioButtonReference.nativeElement.setSelection(true);
          break;
        case 3: //Approved
          this.approvedRadioButtonReference.nativeElement.setSelection(true);
          break;
        case 4: //Rejected
          this.rejectedRadioButtonReference.nativeElement.setSelection(true);
          break;
      }
      let timer = setTimeout(() => {
        this.applyFilteredDataToControls();
        clearTimeout(timer);
      }, 50)
    }
  }

  private applyFilteredDataToControls() {
    if (this.emittedValues !== undefined) {
      if (this.emittedValues.fromDate != '') {
        this.dateRangeComponent.setCustomDisplayDates(this.emittedValues.fromDate, this.emittedValues.toDate);
      }
      if (this.emittedValues.requester.id != '') {
        let value = this.requesters.find(x => this.emittedValues !== undefined && x.id == this.emittedValues.requester.id)
        this.selectedRequesterValue = value;
        let event = {
          detail: {
            selectedItemArray: [value]
          }
        }
        this.setSelectedRequester(event);
      }
      if (this.emittedValues.actionTakenBy.id != '') {
        let value = this.approvers.find(x => this.emittedValues !== undefined && x.id == this.emittedValues.actionTakenBy.id)
        this.selectedActionTakenByValue = value;
        let event = {
          detail: {
            selectedItemArray: [value]
          }
        }
        this.setSelectedActionTakenBy(event);
      }
    }
  }

  private formCancelledState() {
    this.filterForm.get('fromDate')?.addValidators(Validators.required);
    this.filterForm.get('fromDate')?.updateValueAndValidity();
    this.filterForm.get('toDate')?.addValidators(Validators.required);
    this.filterForm.get('toDate')?.updateValueAndValidity();
    this.isDateRangeVisible = true;
    this.showActionTakenBy = false;
  }

  private formApprovedRejectedState() {
    this.filterForm.get('fromDate')?.addValidators(Validators.required);
    this.filterForm.get('fromDate')?.updateValueAndValidity();
    this.filterForm.get('toDate')?.addValidators(Validators.required);
    this.filterForm.get('toDate')?.updateValueAndValidity();
    this.isDateRangeVisible = true;
    this.showActionTakenBy = true;
  }

  private formDefaultState() {
    this.filterForm.get('fromDate')?.clearValidators();
    this.filterForm.get('fromDate')?.updateValueAndValidity();
    this.filterForm.get('toDate')?.clearValidators();
    this.filterForm.get('toDate')?.updateValueAndValidity();
    this.filterForm.patchValue({
      fromDate: ''
    });
    this.filterForm.patchValue({
      toDate: ''
    });
    this.isDateRangeVisible = false;
    this.showActionTakenBy = false;
  }

  private setElementReferences(): void {
    this.openRadioButtonReference = this.radioOptionsReference.find(x => x.nativeElement.id == "openRadioButton")!;
    this.cancelledRadioButtonReference = this.radioOptionsReference.find(x => x.nativeElement.id == "cancelledRadioButton")!;
    this.approvedRadioButtonReference = this.radioOptionsReference.find(x => x.nativeElement.id == "approvedRadioButton")!;
    this.rejectedRadioButtonReference = this.radioOptionsReference.find(x => x.nativeElement.id == "rejectedRadioButton")!;
  }

  private mapEmitOutput(): Filter {
    let value = this.filterForm.value;
    let currentStatus = this.requestStatus.find(x => x.id == value.status)?.description!;
    let requesterName = this.requesters.find(x => x.id == value.requester)?.description!;
    let actionTakenByName = this.approvers.find(x => x.id == value.actionTakenBy)?.description!;
    let output: Filter = {
      status: {
        id: value.status,
        name: currentStatus ? currentStatus : ''
      },
      requester: {
        id: value.requester,
        name: requesterName?.length > 0 ? requesterName[0] : ''
      },
      toDate: value.toDate,
      fromDate: value.fromDate,
      actionTakenBy: {
        id: value.actionTakenBy,
        name: actionTakenByName?.length > 0 ? actionTakenByName[0] : ''
      }
    };
    this.filterQuery.emit(output);
    return output;
  }
}
